import type React from 'react';

import { EventTypeEnum } from '@clubspark-node/clubspark-tennis-types';
import { Grid, Typography } from '@mui/material';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import {
  eventAtomFamily,
  eventWithdrawnDrawParticipantsAtomFamily,
  eventWithdrawnTeamParticipantsAtomFamily,
} from 'atoms/events';
import { useStyles } from 'components/accordion/styles';
import TMXAccordion from 'components/accordion/TMXAccordion';
import StandardPaper from 'components/papers/standard/StandardPaper';
import WithdrawnParticipantsTable from 'components/tables/competitors/withdrawn/WithdrawnParticipantsTable';

const { TEAM } = EventTypeEnum;

const WithdrawnCompetitorsSection: React.FC = () => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const { tournamentId, eventId } = useParams<{ tournamentId: string; eventId: string }>();
  const event = useAtomValue(eventAtomFamily(eventId));
  const withdrawnParticipants = useAtomValue(eventWithdrawnDrawParticipantsAtomFamily(eventId));
  const withdrawnTeamPlayers = useAtomValue(eventWithdrawnTeamParticipantsAtomFamily(eventId));

  const isTeam = event?.eventType === TEAM;

  return (
    <TMXAccordion
      unmountOnClose
      accordionSummaryChildren={
        <Grid container direction="column" spacing={1}>
          <Grid item>
            <Typography sx={{ marginBottom: 0 }} variant="h1">
              {t('withdrawals')}
            </Typography>
          </Grid>
          <Grid container item className={classes.accordionSummaryDetails} spacing={2}>
            <Grid item>
              <Typography variant="body2">{`${t('withdrawnPlayers')} (${withdrawnTeamPlayers.length})`}</Typography>
            </Grid>
            {isTeam && (
              <Grid item>
                <Typography variant="body2">{`${t('withdrawnTeams')} (${withdrawnParticipants.length})`}</Typography>
              </Grid>
            )}
          </Grid>
        </Grid>
      }
      defaultExpanded={false}
    >
      <StandardPaper className="competitors-table">
        <Grid container direction="row" justifyContent="space-between" spacing={4}>
          {isTeam && (
            <Grid item xs={12}>
              <WithdrawnParticipantsTable
                event={event}
                title={t('withdrawnPlayers')}
                tournamentId={tournamentId}
                withdrawnParticipants={withdrawnTeamPlayers}
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <WithdrawnParticipantsTable
              event={event}
              isTeamTable={isTeam}
              title={isTeam ? t('withdrawnTeams') : t('withdrawnPlayers')}
              tournamentId={tournamentId}
              withdrawnParticipants={withdrawnParticipants}
            />
          </Grid>
        </Grid>
      </StandardPaper>
    </TMXAccordion>
  );
};

export default WithdrawnCompetitorsSection;
